import { client, httpGet, httpPost, httpPut } from "./client";
import AssessmentService from "@/services/assessment";
import cache from "./cache";

import {
  formatAssessmentToFloat,
  formatOtherJudgesAssessments,
  formatOtherJudgesAssessmentsAkro,
  formatOtherJudgesAssessmentsRhythmic,
  FormattedAggSubPanelResults,
  FormattedAkroResults,
  FormattedRhythmicResults,
  FormattedJudgePointsData,
  formatTotalPointToString,
  formatZeroPenaltyByRules,
  JudgePointsData,
} from "@/utils/formatters";
import { isChiefJudge } from "@/utils/checks";
import { API } from "@/constants/httpApi";
import { JUDGE_ROLES } from "@/constants/roles";
import axios from "axios";
import {
  AbstractJudgePoints,
  AggSubPanelPoints,
  AggSubPanelResults,
  AkroResultsData,
  RhythmicResultsData,
} from "@/types/results.ts";

export type AggSubPanelType = "TV" | "AV" | "AV-A" | "AV-B" | "EXE";

export type RgSubPanelType = "D" | "E" | "A";

export type BonusCode = "B1" | "B2" | "B3" | "B4" | "B5";

interface JudgeInfo {
  eventStartDate: Date;
  eventEndDate: Date;
  [key: string]: unknown;
}

interface JudgeProfile {
  judgeCode: string;
  role: string;
  [key: string]: unknown;
}

interface PerformanceBlock {
  [key: string]: unknown;
}

interface JudgeAssessmentParams {
  performanceId: string | number;
  judgeCode: string;
}

interface SaveJudgeAssessmentParams {
  performanceId: string | number;
  judgeCode: string;
  judgeRegistrationId?: number;
  submitted?: boolean;
  type: string;
  values: SaveJudgeAssessmentValues;
  version: number;
  isTvInputModeElements?: boolean;
  isAvbInputModeElements?: boolean;
}

type SaveJudgeAssessmentValues = {
  value?: number | string;
  bonus?: boolean;
  bonusCode?: BonusCode | string;
  deductions?: Record<string, number>;
  [key: string]: unknown;
};

interface JudgePointsCorrection {
  [judgeCode: string]: {
    value: number;
    [key: string]: unknown;
  };
}

interface SaveOtherJudgesAssessmentsParams {
  deductions: string;
  version: number;
  judgePointsCorrections?: JudgePointsCorrection;
}

interface SaveAkroJudgesAssessmentsParams extends SaveOtherJudgesAssessmentsParams {
  bonus: boolean;
  bonusValue: number;
}

interface CurrentPerformersParams {
  eventId: string | number;
  statusToken?: number;
}

interface CurrentPerformersResponse {
  request: Promise<unknown>;
  cancelRequest: (_reason?: string) => void;
}

interface JudgeSpecificParams {
  performanceId: string | number;
  judgeCode: string;
  type: string;
  judgeRegistrationId: number;
  values: Record<string, unknown>;
  version: number;
}

interface SubPanelPointsParams {
  performanceId: string | number;
  type: AggSubPanelType;
  subPanelPoints: {
    deductions?: Record<string, number>;
    [key: string]: unknown;
  };
}

interface UnsubmitParams {
  performanceId: string | number;
  judgeCode?: string;
  type?: string;
}

type FeedbackContentType = "PLAIN_TEXT" | "JSON";

interface SaveFeedbackParams {
  performanceId: string | number;
  judgeCode: string;
  feedback: string;
  contentType?: FeedbackContentType;
}

interface FeedbackResponse {
  feedback: string;
  submitted: boolean;
  contentType?: FeedbackContentType;
}

interface MeetingInviteParams {
  performanceId: string | number;
  type: string;
}

const MULTI_VALUE_POINTS_CONTENT_TYPE = "application/vnd.kisanet-jt.multi-value-points+json";
const AGG_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE =
  "application/vnd.kisanet-jt.agg-tv-points-elements+json";
const AGG_AVB_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE =
  "application/vnd.kisanet-jt.agg-avb-points-elements+json";
const httpService = {
  authUser(id: string): Promise<unknown> {
    return client.post(
      `${API.JUDGE_TERMINAL}/authentication?auth_code=${id}`,
      {},
      {
        disabledAuthorizationError: true,
      }
    );
  },
  getCurrentUserState(): Promise<unknown> {
    return client.get(`${API.JUDGE_TERMINAL}/judge-info`);
  },
  logout(): Promise<unknown> {
    return client.post(`${API.JUDGE_TERMINAL}/logout`);
  },
  async getListPerformance(): Promise<PerformanceBlock[]> {
    const { blocks } = await httpGet<{ blocks: PerformanceBlock[] }>(
      `${API.JUDGE_TERMINAL}/performances`
    );
    return blocks;
  },

  getJudgeCodesForPanel(
    performanceId: string | number,
    authKey?: string
  ): Promise<Record<string, string[]>> {
    if (authKey) {
      return client.get(`${API.PUBLIC}/judge-panels`, {
        params: { performanceId, authKey },
        withCredentials: false,
        disabledAuthorizationError: true,
      });
    }
    return client.get(`${API.JUDGE_TERMINAL}/judge-panels?performanceId=${performanceId}`);
  },

  async getJudgeCodesForPanelByType(
    performanceId: string | number,
    type: string
  ): Promise<string[]> {
    const panel = await this.getJudgeCodesForPanel(performanceId);
    if (type in panel) {
      return panel[type];
    }
    throw new Error(`The specified type (${type}) was not found!`);
  },

  async getJudgeCodesForPanelRhythmic(
    performanceId: string | number
  ): Promise<Record<RgSubPanelType, Record<string, string[]>>> {
    const { C: _C, ...panels } = await this.getJudgeCodesForPanel(performanceId);
    const result = {
      D: {},
      E: {},
      A: {},
    } as Record<RgSubPanelType, Record<string, string[]>>;

    Object.keys(panels).forEach((panelKey) => {
      const panelName = panelKey[0] as keyof typeof result;
      result[panelName][panelKey] = panels[panelKey];
    });

    return result;
  },
  async getJudgeCodesForPanelAkro(
    performanceId: string | number
  ): Promise<Record<string, unknown>> {
    const { C: _C, ...panels } = await this.getJudgeCodesForPanel(performanceId);
    return panels;
  },

  getCurrentAndNextPerformers(
    { eventId, statusToken }: CurrentPerformersParams,
    longPoll = false
  ): CurrentPerformersResponse {
    const longPollQuery = longPoll ? `/updates?statusToken=${statusToken}` : "";
    const source = axios.CancelToken.source();
    const request = client.get(`${API.PUBLIC}/current-performers/${eventId}${longPollQuery}`, {
      cancelToken: source.token,
      withCredentials: false,
    });
    const cancelRequest: (_reason?: string) => void = (reason) => {
      source.cancel(reason);
    };

    return {
      request,
      cancelRequest,
    };
  },
  async getJudgeInfo(): Promise<JudgeInfo> {
    const requestName = "getJudgeInfo";

    if (cache.has(requestName)) {
      return cache.get(requestName) as JudgeInfo;
    }

    const {
      eventStartDate: eventStartDateRaw,
      eventEndDate: eventEndDateRaw,
      ...data
    } = await httpGet<{
      eventStartDate: string;
      eventEndDate: string;
      [key: string]: unknown;
    }>(`${API.JUDGE_TERMINAL}/judge-info`);

    const eventStartDate = new Date(eventStartDateRaw);
    const eventEndDate = new Date(eventEndDateRaw);

    const formattedData = {
      ...data,
      eventStartDate,
      eventEndDate,
    };

    cache.set(requestName, formattedData);

    return formattedData;
  },
  async getJudgeProfile(performanceId: string | number): Promise<JudgeProfile> {
    const judgeProfile = await httpGet<{ judgeCode: string; [key: string]: unknown }>(
      `${API.JUDGE_TERMINAL}/judge-profile?performanceId=${performanceId}`
    );

    const role = isChiefJudge(judgeProfile.judgeCode) ? JUDGE_ROLES.CHIEF_JUDGE : JUDGE_ROLES.JUDGE;

    return {
      ...judgeProfile,
      role,
    };
  },

  getJudgeAssessment({
    performanceId,
    judgeCode,
  }: JudgeAssessmentParams): Promise<AbstractJudgePoints> {
    return httpGet<AbstractJudgePoints>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`
    );
  },

  saveJudgeAssessment({
    performanceId,
    judgeCode,
    judgeRegistrationId,
    submitted,
    type,
    values: valuesRaw,
    version,
    isTvInputModeElements,
    isAvbInputModeElements,
  }: SaveJudgeAssessmentParams): Promise<AbstractJudgePoints> {
    const headers = {
      "Content-Type": isTvInputModeElements
        ? AGG_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE
        : isAvbInputModeElements
          ? AGG_AVB_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE
          : "application/json",
    };

    const values = {
      ...valuesRaw,
      deductions: formatZeroPenaltyByRules(valuesRaw.deductions),
    };

    const config = {
      headers,
    };

    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        judgeCode,
        judgeRegistrationId,
        performanceId,
        submitted,
        type,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },

  submitJudgeAssessment({
    performanceId,
    judgeCode,
    judgeRegistrationId,
    submitted,
    type,
    values: valuesRaw,
    version,
    isTvInputModeElements,
    isAvbInputModeElements,
  }: SaveJudgeAssessmentParams): Promise<AbstractJudgePoints> {
    const values = {
      ...valuesRaw,
      deductions: formatZeroPenaltyByRules(valuesRaw.deductions),
    };
    const headers = {
      "Content-Type": isTvInputModeElements
        ? AGG_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE
        : isAvbInputModeElements
          ? AGG_AVB_JUDGE_POINTS_ELEMENTS_CONTENT_TYPE
          : "application/json",
    };

    const config = {
      headers,
    };

    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        judgeCode,
        judgeRegistrationId,
        performanceId,
        submitted,
        type,
        values,
        version,
      },
      config
    );
  },

  async saveOtherJudgesAssessmentsRhythmic(
    performanceId: string | number,
    { deductions: deductionsRaw, version, judgePointsCorrections }: SaveOtherJudgesAssessmentsParams
  ): Promise<unknown> {
    const deductions = formatAssessmentToFloat(deductionsRaw);
    const data = await httpPut<RhythmicResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`,
      {
        deductions,
        version,
        judgePointsCorrections,
      }
    );
    return formatOtherJudgesAssessmentsRhythmic(data);
  },
  getJudgeTanvo(performanceId: string | number, judgeCode: string): Promise<unknown> {
    return client.get(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`
    );
  },
  sendJudgeTanvo({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/vnd.kisanet-jt.tanvo-points+json",
    };
    const config = {
      headers,
    };
    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  saveJudgeTanvo({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/vnd.kisanet-jt.tanvo-points+json",
    };
    const config = {
      headers,
    };
    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  getJudgeLumo(performanceId: string | number, judgeCode: string): Promise<unknown> {
    return client.get(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`
    );
  },
  sendJudgeLumo(judgePoints: JudgeSpecificParams): Promise<AbstractJudgePoints> {
    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${judgePoints.performanceId}/judge-points/${judgePoints.judgeCode}`,
      judgePoints,
      {
        headers: {
          "Content-Type": MULTI_VALUE_POINTS_CONTENT_TYPE,
        },
      }
    );
  },

  saveJudgeLumo(judgePoints: JudgeSpecificParams): Promise<AbstractJudgePoints> {
    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${judgePoints.performanceId}/judge-points/${judgePoints.judgeCode}`,
      judgePoints,
      {
        headers: {
          "Content-Type": MULTI_VALUE_POINTS_CONTENT_TYPE,
        },
      }
    );
  },

  getJudgeSpecialOlympics(performanceId: string | number, judgeCode: string): Promise<unknown> {
    return client.get(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`
    );
  },
  sendJudgeSpecialOlympics({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/json",
    };
    const config = {
      headers,
    };
    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  saveJudgeSpecialOlympics({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/json",
    };
    const config = {
      headers,
    };
    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  getJudgeCompulsoryProgram(performanceId: string | number, judgeCode: string): Promise<unknown> {
    return client.get(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`
    );
  },
  sendJudgeCompulsoryProgram({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/json",
    };
    const config = {
      headers,
    };
    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  saveJudgeCompulsoryProgram({
    performanceId,
    judgeCode,
    type,
    judgeRegistrationId,
    values,
    version,
  }: JudgeSpecificParams): Promise<unknown> {
    const headers = {
      "Content-Type": "application/json",
    };
    const config = {
      headers,
    };
    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}`,
      {
        performanceId,
        judgeCode,
        type,
        judgeRegistrationId,
        values,
        version,
      },
      {
        ...config,
      }
    );
  },
  async saveOtherJudgesAssessmentsAkro(
    performanceId: string | number,
    {
      bonus,
      bonusValue,
      deductions: deductionsRaw,
      version,
      judgePointsCorrections,
    }: SaveAkroJudgesAssessmentsParams
  ): Promise<unknown> {
    const deductions = formatAssessmentToFloat(deductionsRaw);
    const data = await httpPut<AkroResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`,
      {
        bonus,
        bonusValue,
        deductions,
        version,
        judgePointsCorrections,
      }
    );
    return formatOtherJudgesAssessmentsAkro(data);
  },

  async getAggPerformanceResults(
    performanceId: string | number,
    type: AggSubPanelType,
    authKey?: string
  ): Promise<AggSubPanelResults> {
    if (authKey) {
      return client.get(`${API.PUBLIC}/performances/${performanceId}/results/${type}`, {
        params: { authKey },
        withCredentials: false,
        disabledAuthorizationError: true,
      });
    }
    return await httpGet<AggSubPanelResults>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results/${type}`
    );
  },

  getPublicPointsDefinition(
    performanceId: string | number,
    pointsType: string,
    judgeCode: string
  ): Promise<any> {
    return client.get(`${API.PUBLIC}/points-definitions/for-start-list-entry`, {
      params: { startListEntryId: performanceId, pointsType, judgeCode },
      withCredentials: false,
      disabledAuthorizationError: true,
    } as any);
  },

  async getOtherJudgesAssessments(
    performanceId: string | number,
    type: AggSubPanelType,
    isInputModeElements = false
  ): Promise<FormattedJudgePointsData> {
    const data = await this.getAggPerformanceResults(performanceId, type);

    if (!data.sumbitted) {
      AssessmentService.saveAllInitialAssessmentsForAestheticChiefJudge(performanceId, data);
    }
    return formatOtherJudgesAssessments(data as JudgePointsData, isInputModeElements);
  },

  async getRgPerformanceResults(performanceId: string | number): Promise<FormattedRhythmicResults> {
    const results = await httpGet<RhythmicResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`
    );

    if (!results.submitted) {
      AssessmentService.saveAllInitialAssessmentsForRhythmicChiefJudge(performanceId + "", results);
    }
    return formatOtherJudgesAssessmentsRhythmic(results);
  },

  async getOtherAkroJudgesAssessments(
    performanceId: string | number
  ): Promise<FormattedAkroResults> {
    const data = await httpGet<AkroResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`
    );

    return formatOtherJudgesAssessmentsAkro(data);
  },

  async submitOtherJudgesAssessmentsRhythmic(
    performanceId: string | number,
    { deductions: deductionsRaw, version, judgePointsCorrections }: SaveOtherJudgesAssessmentsParams
  ): Promise<unknown> {
    const deductions = formatAssessmentToFloat(deductionsRaw);
    const data = await httpPost<RhythmicResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`,
      {
        deductions,
        version,
        judgePointsCorrections,
      }
    );
    AssessmentService.clearAllInitialAssessmentsForPerformance(performanceId);
    return formatOtherJudgesAssessmentsRhythmic(data);
  },

  async submitPerformanceAssessmentsAkro(
    performanceId: string | number,
    {
      bonus,
      bonusValue,
      deductions: deductionsRaw,
      version,
      judgePointsCorrections,
    }: SaveAkroJudgesAssessmentsParams
  ): Promise<unknown> {
    const deductions = formatAssessmentToFloat(deductionsRaw);
    const data = await httpPost<AkroResultsData>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results`,
      {
        bonus,
        bonusValue,
        deductions,
        version,
        judgePointsCorrections,
      }
    );
    AssessmentService.clearAllInitialAssessmentsForPerformance(performanceId);
    return formatOtherJudgesAssessmentsAkro(data);
  },

  async saveAggSubPanelPoints(
    performanceId: string | number,
    type: AggSubPanelType,
    subPanelPoints: AggSubPanelPoints
  ): Promise<FormattedAggSubPanelResults> {
    const subPanelPointsCopy = {
      ...subPanelPoints,
      deductions: formatZeroPenaltyByRules(subPanelPoints.deductions),
    };

    const { total: totalRaw, ...data } = await httpPut<AggSubPanelResults>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results/${type}`,
      subPanelPointsCopy
    );

    const total = formatTotalPointToString(totalRaw);

    return {
      ...data,
      total,
    };
  },

  async submitOtherJudgesAssessments({
    performanceId,
    type,
    subPanelPoints: subPanelPointsRaw,
  }: SubPanelPointsParams): Promise<FormattedAggSubPanelResults> {
    const subPanelPoints = {
      ...subPanelPointsRaw,
      deductions: formatZeroPenaltyByRules(subPanelPointsRaw.deductions),
    };

    const { total: totalRaw, ...data } = await httpPost<AggSubPanelResults>(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results/${type}`,
      subPanelPoints
    );

    const total = formatTotalPointToString(totalRaw);
    AssessmentService.clearAllInitialAssessmentsForPerformance(performanceId + "");
    return {
      ...data,
      total,
    };
  },

  unsubmitChiefJudgeAssessment({
    performanceId,
    judgeCode,
  }: UnsubmitParams): Promise<AbstractJudgePoints> {
    return client.delete(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-points/${judgeCode}/submitted`
    );
  },

  unsubmitJudgeAssessment({ performanceId, type }: UnsubmitParams): Promise<unknown> {
    return client.delete(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/results/${type}/submitted-judge-points`
    );
  },

  createMeetingInvite({ performanceId, type }: MeetingInviteParams): Promise<unknown> {
    return client.put(
      `${API.JUDGE_TERMINAL}/meeting-invitations?performanceId=${performanceId}&subPanelType=${type}`
    );
  },

  clearCache(): void {
    cache.clear();
  },

  getFeedback({
    performanceId,
    judgeCode,
  }: Omit<SaveFeedbackParams, "feedback">): Promise<FeedbackResponse> {
    return client.get(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-feedback/${judgeCode}`
    );
  },

  saveJudgeFeedback({
    performanceId,
    judgeCode,
    feedback,
    contentType,
  }: SaveFeedbackParams): Promise<FeedbackResponse> {
    return client.put(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-feedback/${judgeCode}`,
      { feedback, ...(contentType && { contentType }) }
    );
  },

  submitJudgeFeedback({
    performanceId,
    judgeCode,
    feedback,
    contentType,
  }: SaveFeedbackParams): Promise<FeedbackResponse> {
    return client.post(
      `${API.JUDGE_TERMINAL}/performances/${performanceId}/judge-feedback/${judgeCode}`,
      { feedback, ...(contentType && { contentType }) }
    );
  },
};

export default httpService;
